import { useRoutes, useNavigate, NavigateFunction } from 'react-router-dom';
import { useEffect, Suspense } from 'react';
import routes from './config';

// Global navigate function
declare global {
  interface Window {
    REACT_APP_NAVIGATE: NavigateFunction | null;
  }
}

window.REACT_APP_NAVIGATE = null;

let navigateResolve: ((navigate: NavigateFunction) => void) | null = null;
export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolve = resolve;
});

// Enhanced loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-6"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-b-emerald-400 rounded-full animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
        <p className="text-gray-700 font-semibold text-lg mb-2">Loading Page...</p>
        <p className="text-gray-500 text-sm">Please wait a moment</p>
      </div>
    </div>
  );
}

// Error fallback for lazy loading failures
function LazyLoadError() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Failed</h2>
        <p className="text-gray-600 mb-6">
          We couldn't load this page. This might be due to a network issue.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Reload Page
        </button>
      </div>
    </div>
  );
}

export function AppRoutes() {
  const navigate = useNavigate();
  const element = useRoutes(routes);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    if (navigateResolve) {
      navigateResolve(navigate);
      navigateResolve = null;
    }
  }, [navigate]);

  return (
    <Suspense fallback={<PageLoader />}>
      {element || <LazyLoadError />}
    </Suspense>
  );
}
